import "./CartSubtitle.css";
import React, { useEffect, useState, useRef, useContext, useMemo } from "react";
import { motion } from "framer-motion";
import {
  FinancialData,
  ItemDetailsData,
  RecommendedItem,
} from "../../../../Utils/DefinitionsInterfaces";
import Cart from "../Cart/Cart";
import FormCluster from "../FormCluster/FormCluster";
import FormCluster2 from "../FormCluster/FormCluster2";
import { formatExamDate } from "../../../../Utils/dateFormatter";
import ItemInfoModal from "../ItemInfoModal/ItemInfoModal";
import { CartContext } from "../../../../App";
import { doNFOTransactionResponse } from "../../../../Utils/CheckoutAPIs/CheckoutAPIs";

interface CartSubtitleProps {
  stateDefaultItems: FinancialData[];
  stateCheckoutItems: ItemDetailsData[];
  stateRecommendedItems: RecommendedItem[];
  cbRemoveProduct: (itemId: number) => void;
  cbAddRecommended: (itemId: number) => void;
  cbHandlePaymentClick: (data?: doNFOTransactionResponse) => void;
}

const CartSubtitle = (props: CartSubtitleProps) => {
  const [totalSellingPrice, setTotalSellingPrice] = useState<number>(0);
  const [totalBasePrice, setTotalBasePrice] = useState<number>(0);
  const formCluster2Ref = useRef<HTMLDivElement>(null);
  const [itemInfoVisible, setItemInfoVisible] = useState(false);
  const [currentItemIndex, setCurrentItemIndex] = useState<number>(0);
  const { bundleInfo, checkOutItems } = useContext(CartContext);

  // Check if bundle pricing is active and primary product is in cart
  const isBundlePricingActive = () => {
    if (!bundleInfo.isBundle || !bundleInfo.primaryProductId) return false;

    return checkOutItems.some(
      (item) => item.id === bundleInfo.primaryProductId
    );
  };

  // Bundle pricing header component - memoized to prevent unnecessary re-renders
  const BundlePricingHeader = useMemo(() => {
    if (props.stateRecommendedItems.filter((e) => e.id !== 3).length === 0) {
      return null;
    }

    // Get the lowest discounted bundle price to show in header
    const lowestBundlePrice = Math.min(
      ...props.stateRecommendedItems
        .filter((e) => e.id !== 3)
        .map((item) => item.discountedBundlePrice)
    );

    // Get the total bundle price (sum of all discounted bundle prices)
    const totalBundlePrice = props.stateRecommendedItems
      .filter((e) => e.id !== 3)
      .reduce(
        (total, item) => item.selling_price + item.discountedBundlePrice,
        0
      );

    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        style={{
          backgroundColor: "rgba(36, 108, 193, 0.05)",
          border: "1px solid rgba(36, 108, 193, 0.15)",
          borderTopLeftRadius: "12px",
          borderTopRightRadius: "12px",
          padding: "12px 16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "90%",
        }}
      >
        <div
          style={{
            color: "rgba(36, 108, 193, 1)",
            fontSize: "16px",
            fontWeight: "500",
            textAlign: "center",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "8px",
            width: "100%",
          }}
        >
          <div
            style={{
              backgroundColor: "rgba(36, 108, 193, 0.1)",
              padding: "4px 8px",
              borderRadius: "6px",
              fontSize: "12px",
              fontWeight: "600",
            }}
          >
            🎉 Bundle Deal
          </div>
          <div
            style={{
              fontSize: "14px",
              fontWeight: "600",
            }}
          >
            Special price of ₹{totalBundlePrice} for 2 books (₹
            {lowestBundlePrice} for 2nd book)
          </div>
        </div>
      </motion.div>
    );
  }, [props.stateRecommendedItems, isBundlePricingActive()]);

  // Helper function to get display price for recommended items
  const getRecommendedItemPrice = (item: RecommendedItem) => {
    return item.selling_price;
  };

  // Helper function to get discount percentage for recommended items
  const getDiscountPercentage = (item: RecommendedItem) => {
    const displayPrice = getRecommendedItemPrice(item);
    return Math.round(
      ((item.base_price - displayPrice) / item.base_price) * 100
    );
  };

  const handleShowItemInfo = (index: number) => {
    setCurrentItemIndex(index);
    setItemInfoVisible(true);
    sessionStorage.setItem("currentItemIndex", index.toString());
  };

  const handleCloseItemInfo = () => {
    setItemInfoVisible(false);
    sessionStorage.removeItem("currentItemIndex");
  };

  const handleRecommendedClick = (itemId: number) => {
    props.cbAddRecommended(itemId);
    // if (itemId === 3) {
    //   formCluster2Ref.current?.scrollIntoView({
    //     behavior: "smooth",
    //     block: "start",
    //   });
    // }
  };

  useEffect(() => {
    let sumSellingPrice = 0;
    let sumBasePrice = 0;
    for (let i = 0; i < props.stateDefaultItems.length; i++) {
      sumSellingPrice += props.stateDefaultItems[i].selling_price;
      sumBasePrice += props.stateDefaultItems[i].base_price;
    }
    for (let i = 0; i < props.stateCheckoutItems.length; i++) {
      sumSellingPrice += props.stateCheckoutItems[i].selling_price;
      sumBasePrice += props.stateCheckoutItems[i].base_price;
    }
    setTotalBasePrice(sumBasePrice);
    setTotalSellingPrice(sumSellingPrice);
  }, [
    props.stateDefaultItems,
    props.stateCheckoutItems,
    props.stateRecommendedItems,
  ]);

  return (
    <div className="cart-parent-wrapper">
      <div className="recommended-items-mobile">
        {BundlePricingHeader}
        {props.stateRecommendedItems
          .filter((e) => e.id !== 3)
          .map((e, i) => {
            return (
              <motion.div
                key={`recommended-item-${i}`}
                initial={{ scale: 0.9 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.3 }}
                whileHover={{ scale: 1.01 }}
                style={{
                  backgroundColor: "rgba(255, 255, 255, 1)",
                  border: "solid 1px rgba(0,0,0,0.1)",
                  width: "100%",
                  padding: "10px 10px",
                  borderRadius: "20px",
                  marginBottom: "15px",
                  cursor: "pointer",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    marginBottom: "10px",
                  }}
                >
                  <div
                    style={{
                      color: "rgba(36, 108, 193, 1)",
                      alignSelf: "center",
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      fontSize: "12px",
                      backgroundColor: "rgba(36, 108, 193, 0.1)",
                      width: "fit-content",
                      padding: "6px 6px",
                      borderRadius: "8px",
                    }}
                  >
                    Recommended Items
                  </div>
                  {e.id === 3 && (
                    <div
                      onClick={() => handleShowItemInfo(i)}
                      style={{
                        backgroundColor: "rgba(36, 108, 193, 1)",
                        minWidth: "22px",
                        minHeight: "22px",
                        maxWidth: "22px",
                        maxHeight: "22px",
                        marginLeft: "10px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: "100px",
                      }}
                    >
                      <span style={{ color: "white", fontSize: "12px" }}>
                        i
                      </span>
                    </div>
                  )}
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: "10px",
                    alignItems: "flex-end",
                  }}
                >
                  <div
                    className="text-wrapper-3"
                    style={{
                      maxWidth: "250px",
                      color: "rgba(36, 108, 193, 1)",
                      alignSelf: "center",
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      gap: "10px",
                    }}
                  >
                    <img
                      src={e.image}
                      alt={e.name}
                      style={{ width: "50px", height: "50px" }}
                    />
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        fontSize: "14px",
                      }}
                    >
                      {e.name}
                      <span
                        style={{
                          marginTop: "5px",
                          backgroundColor: "rgba(2, 59, 143, 0.1)",
                          color: "rgba(2, 59, 143, 1)",
                          padding: "4px 8px",
                          borderRadius: "4px",
                          fontSize: "12px",
                          fontWeight: "500",
                          width: "fit-content",
                        }}
                      >
                        {getDiscountPercentage(e)}% OFF
                      </span>
                      {e.id === 3 && (
                        <div
                          style={{
                            color: "rgba(36, 108, 193, 0.8)",
                            fontSize: "12px",
                            fontWeight: "400",
                            fontFamily: "OggTextMedium",
                            marginTop: "5px",
                          }}
                        >
                          Exam Scheduled on : <br />
                          {e.examDate && `${formatExamDate(e.examDate)}`}
                        </div>
                      )}
                    </div>
                  </div>

                  <div
                    onClick={() => {
                      handleRecommendedClick(e.id);
                    }}
                    style={{ display: "flex", flexDirection: "column" }}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        justifyContent: "flex-end",
                      }}
                    >
                      <span
                        style={{
                          textDecoration: "line-through",
                          marginRight: "5px",
                          color: "rgba(138, 138, 138, 0.8)",
                          fontSize: "12px",
                        }}
                        className="text-wrapper-3"
                      >
                        {"₹" + e.base_price}
                      </span>
                      <span
                        style={{ fontSize: "18px" }}
                        className="text-wrapper-3"
                      >
                        {"₹" + e.discountedBundlePrice}
                      </span>
                    </div>
                    <div
                      style={{
                        backgroundColor: " rgba(138, 138, 138, 0.1)",
                        border: "0.56px solid rgba(138, 138, 138, 0.1)",
                        padding: "8px",
                        marginTop: "10px",
                        color: "rgba(2, 59, 143, 1)",
                        alignItems: "center",
                        justifyContent: "center",
                        display: "flex",
                        cursor: "pointer",
                        borderRadius: "10px",
                        fontSize: "14px",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Add to cart now
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
      </div>
      <div className="cart-subtitle">
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <FormCluster2 />
          <div className="delivery-address-title">
            <div className="delivery-address-title-text">Delivery Address</div>
            <div className="line-divider"></div>
          </div>

          <FormCluster />
        </div>
        <div className="outer-container">
          <div className="inner-divider"></div>
        </div>
        <div className="recommended-items-container">
          <div className="recommended-items">
            {BundlePricingHeader}
            {props.stateRecommendedItems
              .filter((e) => e.id !== 3)
              .map((e, i) => {
                return (
                  <motion.div
                    key={`recommended-item-${i}`}
                    initial={{ scale: 0.9 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 0.3 }}
                    whileHover={{ scale: 1.01 }}
                    style={{
                      backgroundColor: "rgba(255, 255, 255, 1)",
                      border: "solid 1px rgba(0,0,0,0.1)",
                      width: "100%",
                      padding: "20px 25px",
                      borderRadius: "20px",
                      marginBottom: "15px",
                      cursor: "pointer",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        justifyContent: "flex-start",
                        marginBottom: "10px",
                      }}
                    >
                      <div
                        style={{
                          color: "rgba(36, 108, 193, 1)",
                          alignSelf: "center",
                          display: "flex",
                          flexDirection: "row",
                          alignItems: "center",
                          fontSize: "18px",

                          backgroundColor: "rgba(36, 108, 193, 0.1)",
                          width: "fit-content",
                          padding: "10px 10px",
                          borderRadius: "10px",
                        }}
                      >
                        Recommended Items
                      </div>
                      {e.id === 3 && (
                        <div
                          onClick={() => handleShowItemInfo(i)}
                          style={{
                            backgroundColor: "rgba(36, 108, 193, 1)",
                            minWidth: "30px",
                            minHeight: "30px",
                            maxWidth: "30px",
                            maxHeight: "30px",
                            marginLeft: "10px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            borderRadius: "100px",
                          }}
                        >
                          <span style={{ color: "white", fontSize: "20px" }}>
                            i
                          </span>
                        </div>
                      )}
                    </div>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <div
                        className="text-wrapper-3"
                        style={{
                          maxWidth: "250px",
                          color: "rgba(36, 108, 193, 1)",
                          alignSelf: "center",
                          display: "flex",
                          flexDirection: "row",
                          alignItems: "center",
                          gap: "10px",
                        }}
                      >
                        <img
                          src={e.image}
                          alt={e.name}
                          style={{ width: "50px", height: "50px" }}
                        />
                        <div
                          style={{ display: "flex", flexDirection: "column" }}
                        >
                          {e.name}
                          <span
                            style={{
                              marginTop: "5px",
                              backgroundColor: "rgba(2, 59, 143, 0.1)",
                              color: "rgba(2, 59, 143, 1)",
                              padding: "4px 8px",
                              borderRadius: "4px",
                              fontSize: "14px",
                              fontWeight: "500",
                              width: "fit-content",
                            }}
                          >
                            {Math.round(
                              ((e.base_price - e.discountedBundlePrice) /
                                e.base_price) *
                                100
                            )}
                            % OFF
                          </span>
                          {e.id === 3 && (
                            <div
                              style={{
                                color: "rgba(36, 108, 193, 0.8)",
                                fontSize: "14px",
                                fontWeight: "400",
                                fontFamily: "OggTextMedium",
                                marginTop: "5px",
                              }}
                            >
                              Exam Scheduled on : <br />
                              {e.examDate && `${formatExamDate(e.examDate)}`}
                            </div>
                          )}
                        </div>
                      </div>

                      <div
                        onClick={() => {
                          handleRecommendedClick(e.id);
                        }}
                        style={{ display: "flex", flexDirection: "column" }}
                      >
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "center",
                          }}
                        >
                          <span
                            style={{
                              textDecoration: "line-through",
                              marginRight: "10px",
                              color: "rgba(138, 138, 138, 0.8)",
                            }}
                            className="text-wrapper-3"
                          >
                            {"₹" + e.base_price}
                          </span>
                          <span className="text-wrapper-3">
                            {"₹" + e.discountedBundlePrice}
                          </span>
                        </div>
                        <div
                          style={{
                            backgroundColor: " rgba(138, 138, 138, 0.1)",
                            border: "0.56px solid rgba(138, 138, 138, 0.1)",
                            padding: "8px",
                            marginTop: "10px",
                            color: "rgba(2, 59, 143, 1)",
                            alignItems: "center",
                            justifyContent: "center",
                            display: "flex",
                            cursor: "pointer",
                            borderRadius: "10px",
                          }}
                        >
                          Add to cart now
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
          </div>
          <Cart
            stateDefaultItems={props.stateDefaultItems}
            stateCheckoutItems={props.stateCheckoutItems}
            stateRecommendedItems={props.stateRecommendedItems}
            cbRemoveProduct={props.cbRemoveProduct}
            cbAddRecommended={props.cbAddRecommended}
            cbHandlePaymentClick={props.cbHandlePaymentClick}
          />
        </div>
      </div>

      <ItemInfoModal
        examDate={props.stateRecommendedItems[currentItemIndex]?.examDate || ""}
        image={props.stateRecommendedItems[currentItemIndex]?.image || ""}
        title={props.stateRecommendedItems[currentItemIndex]?.name || ""}
        description={""}
        isVisible={itemInfoVisible}
        currentItemId={props.stateRecommendedItems[currentItemIndex]?.id || 0}
        onClose={handleCloseItemInfo}
        cbHandleCloseItemInfo={handleCloseItemInfo}
        cbAddRecommended={props.cbAddRecommended}
      >
        <div></div>
      </ItemInfoModal>
    </div>
  );
};

export default CartSubtitle;
